import React from 'react';
import { EnabledExportPages, ProductsTabView, ViewType } from '@hooks/export/Model';
import { useExportModal } from '@hooks/export/export-modal/UseExportModal';
import { CreateViewStateFn, ExportFormat } from '@ecosystem/newron-client-lib';
import {
  dataExportInitiatedTrackingFunction,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { getBarChartKpiDisplayName, SaleType } from '@models/Products';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { SaleTypeMappingProducts } from '@const/PerformanceType';
import { WithExportModal } from '@components/design';
import { ToggleViewButtons } from '@components/toggle-view-buttons/ToggleViewButton';
import { PageType, SelectedFilters } from '@utils/pageConfig/Types';
import ProductsLeaderboardCollectionButton from '@components/products-leaderboard-collection-button/ProductsLeaderboardCollectionButton';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { ProductsLeaderboardDoubleBarChartInsight } from '@components/collections/products-leaderboard-double-bar-chart/Model';

export interface MarketCardHeaderIconProps {
  isExportButtonDisabled: boolean;
  onChartDownload: (exportFormats: ExportFormat[]) => Promise<void>;
  isTableView: boolean;
  saleType: SaleType;
  onChange: (view: ViewType) => void;
  createInsightDataFn: CreateInsightDataFn<ProductsLeaderboardDoubleBarChartInsight>;
  createViewStateFn?: CreateViewStateFn<any>;
}

const getSelectedCountryAndProductGroupString = (selectedFilters: SelectedFilters[]): string => {
  const filters = selectedFilters[PageType.RI_PRODUCT]?.selectedFilters ?? [];

  const country = filters.find((f) => f.dataType === 'COUNTRY')?.options?.[0]?.value ?? '';
  const productGroup = filters.find((f) => f.dataType === 'PRODUCT GROUP')?.options?.[0]?.value ?? '';

  return [country, productGroup].filter(Boolean).join('_');
};

const SellingProductsHeaderIcon: React.FC<MarketCardHeaderIconProps> = ({
  isExportButtonDisabled,
  onChartDownload,
  isTableView,
  saleType,
  onChange,
  createInsightDataFn,
  createViewStateFn,
}) => {
  const pagesConfig = usePageConfig();

  const selectedCountryAndProductGroup = getSelectedCountryAndProductGroupString(pagesConfig.appliedFilters);

  const viewType = isTableView ? ViewType.Table : ViewType.BarChart;

  const {
    openExportModal,
    onCloseExportModal,
    onExportError,
    exportModalFormats,
    exportModalMessage,
    exportModalHeaderText,
    isExportModalOpen,
  } = useExportModal({
    currentPage: EnabledExportPages.Products,
    currentTab: ProductsTabView.Leaderboard,
    currentView: viewType,
    currentCountryAndProduct: selectedCountryAndProductGroup,
  });
  //Amplitude tracking

  const handleDataExportTrackingFunction = useDataExportTracking('mainFiltersProducts', 'hitlistsRetailerClient');

  const handleExportTracking = () => {
    const elementKpi = isTableView
      ? SaleTypeMappingProducts[saleType]
      : getBarChartKpiDisplayName(pagesConfig.sortingOptionsProducts.headerKey);
    handleDataExportTrackingFunction(
      {
        elementType: isTableView ? ElementType.TABLE : ElementType.BAR_CHART,
        elementKpi: elementKpi,
        elementSort: pagesConfig.sortingOptionsProducts,
      },
      dataExportInitiatedTrackingFunction,
    );
  };
  const onExportClick = () => {
    handleExportTracking();
    openExportModal();
  };

  return (
    <span className="flex items-center gap-1">
      {!isTableView && (
        <ProductsLeaderboardCollectionButton
          isDisabled={isExportButtonDisabled}
          createInsightDataFn={createInsightDataFn}
          createViewStateFn={createViewStateFn}
        />
      )}
      <ToggleViewButtons
        {...{
          view: viewType,
          isExportDisabled: isExportButtonDisabled,
          onExportClick,
          onChange,
        }}
      />

      <WithExportModal
        isShown={isExportModalOpen}
        exportFormats={exportModalFormats}
        message={exportModalMessage}
        title={exportModalHeaderText}
        download={onChartDownload}
        onClose={onCloseExportModal}
        onError={onExportError}
      />
    </span>
  );
};

export default SellingProductsHeaderIcon;
